// Theme runtime — "light" (day in the savanna) / "dark" (night in the savanna),
// and since 2026-09-23 a third preference: "auto", which follows the phone.
// The attribute values are the spec's: design-source/suricata-design-language.html
// keys its theme blocks on [data-theme="light"] and [data-theme="dark"].
//
// Two different questions, two different functions:
//   getThemePref() — what the user CHOSE: light | dark | auto
//   getTheme()     — what is on screen right now: light | dark
// "auto" is a preference, never a value: nothing in the CSS knows about it,
// because the attribute always carries a resolved theme.
//
// The boot script in public/index.html stamps data-theme before first paint.
// It treats any stored value that is not exactly "light" or "dark" as "follow
// the OS", so "auto" needs no change there and no flash on reload.
//
// This module is the one writer after boot: the header toggle and the settings
// card call setThemePref, and everything colour-related follows through the
// CSS variables.

import { useState, useEffect } from "react";

const KEY = "si_theme";

// Keep in sync with --si-surface-page in src/index.css (DESIGN.md palette).
const THEME_COLOR = { light: "#F7F1E6", dark: "#0B1220" };

const mq = () => (typeof window !== "undefined" && window.matchMedia
  ? window.matchMedia("(prefers-color-scheme: dark)")
  : null);

export function systemTheme() {
  return mq()?.matches ? "dark" : "light";
}

// What the user chose. Anything unrecognised — including nothing at all, a
// first visit — is "auto": the honest answer for "we were never told".
export function getThemePref() {
  let saved = null;
  try { saved = localStorage.getItem(KEY); } catch { /* private mode */ }
  return saved === "light" || saved === "dark" ? saved : "auto";
}

// What is actually on screen.
export function getTheme() {
  const attr = document.documentElement.getAttribute("data-theme");
  return attr === "dark" ? "dark" : "light";
}

function apply(theme) {
  const t = theme === "dark" ? "dark" : "light";
  document.documentElement.setAttribute("data-theme", t);
  // Address-bar / installed-window chrome follows the active theme. The two
  // static media-query tags in index.html cover first paint; once the app is
  // running they would fight an explicit choice, so they are replaced by one
  // managed tag — which under "auto" is simply kept in step with the OS.
  document.querySelectorAll('meta[name="theme-color"]').forEach((m, i) => {
    if (i > 0) m.remove();
    else { m.removeAttribute("media"); m.setAttribute("content", THEME_COLOR[t]); }
  });
}

export function setThemePref(pref) {
  const p = pref === "light" || pref === "dark" ? pref : "auto";
  try { localStorage.setItem(KEY, p); } catch { /* private mode — session only */ }
  apply(p === "auto" ? systemTheme() : p);
  notify();
}

// The OS switching while the user is on "auto" has to reach the screen. One
// listener for the whole app, installed on import: a component that mounts
// later cannot miss a change that happened before it existed.
const listeners = new Set();
function notify() { listeners.forEach(fn => fn()); }
if (typeof window !== "undefined") {
  const m = mq();
  m?.addEventListener?.("change", () => {
    if (getThemePref() === "auto") { apply(systemTheme()); notify(); }
  });
}

// Live preference + resolved theme, for anything that renders the choice.
export function useThemePref() {
  const [state, setState] = useState(() => ({ pref: getThemePref(), theme: getTheme() }));
  useEffect(() => {
    const sync = () => setState({ pref: getThemePref(), theme: getTheme() });
    listeners.add(sync);
    // A second tab, or the attribute changed by anything else.
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    sync();
    return () => { listeners.delete(sync); obs.disconnect(); };
  }, []);
  return state;
}

// Live theme for the few components that cannot read a CSS variable: anything
// rendered outside our stylesheet's reach — the TradingView iframe, a canvas.
// Everything else should consume UI.* tokens and needs no hook at all.
export function useTheme() {
  return useThemePref().theme;
}

// Resolve a CSS variable to its concrete value — for the same cross-boundary
// cases as useTheme (an iframe config object cannot hold a var() reference).
export function cssVar(name) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}
